/* eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AmplifyAlertBanner } from '../../../components/AmplifyAlertBanner';
import { Table, Space, Input, Modal } from 'antd';
import { Button } from '@aws-amplify/ui-react';
import { EditOutlined } from '@mui/icons-material';
import CMS from '../../../cms/cms.json';
import { Auth } from 'aws-amplify';
import usersService from '../../../axios/usersService';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VpnKeyOffIcon from '@mui/icons-material/VpnKeyOff';
import BlockIcon from '@mui/icons-material/Block';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import sigmaService from '../../../axios/sigmaService';
import { axiosRequest } from '../../../utils/axiosRequest';
import { setSupplierAdmin } from '../../../graphql/mutations';

const PAGE_SIZE = 8;

export const ManageUser = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [showInternalUsers, setShowInternalUsers] = useState(false);
  const [showConfirmationModal, setConfirmationModal] = useState(false);
  const [modalMsg, setModalMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ variation: '', msg: '' });
  const [currentRow, setCurrentRow] = useState({});
  const [managing, setManaging] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const searchDebounceRef = useRef(null);

  const columns = [
    {
      title: 'First Name',
      align: 'center',
      dataIndex: 'firstName',
    },
    {
      title: 'Last Name',
      align: 'center',
      dataIndex: 'lastName',
    },
    {
      title: 'Phone',
      align: 'center',
      dataIndex: 'phone',
    },
    {
      title: 'Email',
      align: 'center',
      dataIndex: 'mail',
    },
    {
      title: 'Vendor',
      align: 'center',
      dataIndex: 'vendorName',
    },
    {
      title: 'Created',
      align: 'center',
      dataIndex: 'createdAt',
    },
    {
      title: 'Edit',
      align: 'center',
      key: 'action',
      render: (_, row) => (
        <Space>
          <Button
            key="Edit"
            title="Edit"
            variation="link"
            size="small"
            onClick={() => {
              navigate('/edit-user', { state: { email: row.mail } });
            }}
          >
            <EditOutlined />
          </Button>
          {row.active ? (
            <Button
              key="Deactivate User"
              title="Deactivate User"
              variation="link"
              size="small"
              onClick={() => {
                setManaging('status');
                setCurrentRow(row);
                setModalMsg('Do you want to deactivate this user?');
                handleModal(true);
              }}
            >
              <BlockIcon />
            </Button>
          ) : (
            <Button
              key="Activate User"
              title="Activate User"
              variation="link"
              size="small"
              onClick={() => {
                setManaging('status');
                setCurrentRow(row);
                setModalMsg('Do you want to activate this user?');
                handleModal(true);
              }}
            >
              <ManageAccountsIcon />
            </Button>
          )}
          {row.vendorName && row.vendorName !== 'INTERNAL' && (
            row.isSupplierAdmin ? (
              <Button
                key="Supplier Admin"
                title="This user is the Supplier Admin"
                variation="primary"
                size="small"
                disabled={true}
              >
                <AdminPanelSettingsIcon />
              </Button>
            ) : (
              <Button
                key="Set Supplier Admin"
                title="Set as Supplier Admin"
                variation="link"
                size="small"
                onClick={() => {
                  setManaging('supplierAdmin');
                  setCurrentRow(row);
                  setModalMsg('Do you want to set this user as supplier admin? This will remove the current supplier admin for this vendor.');
                  handleModal(true);
                }}
              >
                <AdminPanelSettingsOutlinedIcon />
              </Button>
            )
          )}
        </Space>
      ),
    },
  ];

  const columnsInternal = [
    {
      title: 'First Name',
      align: 'center',
      dataIndex: 'username',
    },
    {
      title: 'Last Name',
      align: 'center',
      dataIndex: 'lastName',
    },
    {
      title: 'Phone',
      align: 'center',
      dataIndex: 'phone',
    },
    {
      title: 'Email',
      align: 'center',
      dataIndex: 'mail',
    },
    {
      title: 'Vendor',
      align: 'center',
      dataIndex: 'vendorName',
    },
    {
      title: 'Action',
      align: 'center',
      key: 'action',
      render: (_, row) => (
        <Space>
          {row.vendorName === 'INTERNAL' ? (
            <Button
              key="Promote to Admin"
              title="Promote to Admin"
              variation="link"
              size="small"
              onClick={() => {
                setManaging('admin');
                setCurrentRow(row);
                setModalMsg('Do you want to promote this user as Admin?');
                handleModal(true);
              }}
            >
              <VpnKeyIcon />
            </Button>
          ) : (
            <Button
              key="Demote to normal Internal User"
              title="Demote to normal Internal User"
              variation="link"
              size="small"
              onClick={() => {
                setManaging('admin');
                setCurrentRow(row);
                setModalMsg(
                  'Do you want to demote this user as Internal user?',
                );
                handleModal(true);
              }}
            >
              <VpnKeyOffIcon />
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const loadUsers = useCallback(
    async (page, pageSize, search, isInternal) => {
      setLoading(true);
      try {
        const usersListRes = isInternal
          ? await usersService.fetchInternalUsers(page, pageSize, search)
          : await usersService.getUsers(page, pageSize, search);

        const items = (usersListRes?.data ?? []).map((item) => ({
          ...item,
          key: item.mail,
        }));

        setFilteredData(items);
        setPagination({
          current: page,
          pageSize,
          total: usersListRes?.totalCount ?? 0,
        });
      } catch (err) {
        console.log('Error encountered when fetching users: ', err);
        setConfig({ variation: 'error', msg: 'Error fetching users' });
        setFilteredData([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadUsers(1, PAGE_SIZE, searchTerm, showInternalUsers);
  }, [showInternalUsers]);

  useEffect(() => {
    if (refreshKey === 0) {
      return;
    }
    loadUsers(pagination.current, pagination.pageSize, searchTerm, showInternalUsers);
  }, [refreshKey]);

  useEffect(() => {
    if (location.state) {
      const { variation, msg } = location.state;
      setConfig({ variation: variation, msg: msg });
    }
  }, []);

  const triggerRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleTableChange = (tablePagination) => {
    const page = tablePagination.current;
    const pageSize = tablePagination.pageSize;
    loadUsers(page, pageSize, searchTerm, showInternalUsers);
  };

  const handleSearchChange = (event) => {
    const value = event?.target?.value ?? '';
    setSearchTerm(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      loadUsers(1, pagination.pageSize, value, showInternalUsers);
    }, 300);
  };

  const handleSearch = (value) => {
    const searchValue = value ?? '';
    setSearchTerm(searchValue);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    loadUsers(1, pagination.pageSize, searchValue, showInternalUsers);
  };

  const handleUserTypeToggle = () => {
    setShowInternalUsers(!showInternalUsers);
    setSearchTerm('');
    setPagination({ current: 1, pageSize: PAGE_SIZE, total: 0 });
  };

  const promoteUser = async (row) => {
    handleModal(false);
    setLoading(true);
    const resPromote = await usersService.manageInternalAdmin(
      row.mail,
      'promote',
    );
    if (resPromote) {
      triggerRefresh();
    } else {
      setLoading(false);
      setConfig({
        variation: 'error',
        msg: 'Something went wrong, please contact admin',
      });
    }
  };

  const demoteUser = async (row) => {
    handleModal(false);
    setLoading(true);
    const resPromote = await usersService.manageInternalAdmin(
      row.mail,
      'demote',
    );
    if (resPromote) {
      triggerRefresh();
    } else {
      setLoading(false);
      setConfig({
        variation: 'error',
        msg: 'Something went wrong, please contact admin',
      });
    }
  };

  const handleModal = (value) => {
    setConfirmationModal(value);
  };

  const activateUser = async (email) => {
    handleModal(false);
    setLoading(true);
    await usersService
      .manageUserStatus(email, 'activate')
      .then((res) => {
        if (res?.data?.data?.manageUserStatus) {
          triggerRefresh();
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('ERROR editing user: ', err);
        setLoading(false);
        setConfig({
          variation: 'error',
          msg: 'Error activating user, please contact admin',
        });
      });
    handleModal(false);
  };

  const deactivateUser = async (email) => {
    handleModal(false);
    setLoading(true);
    await usersService
      .manageUserStatus(email, 'deactivate')
      .then((res) => {
        if (res?.data?.data?.manageUserStatus) {
          sigmaService.deactivateUser(email);
          triggerRefresh();
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('ERROR editing user: ', err);
        setLoading(false);
        setConfig({
          variation: 'error',
          msg: 'Error deactivating user, please contact admin',
        });
      });
  };

  const manageSupplierAdmin = async (row) => {
    handleModal(false);
    setLoading(true);

    try {
      const isCurrentlyAdmin = row.isSupplierAdmin;

      const session = await Auth.currentSession();
      const idToken = session.getIdToken();
      const jwt = idToken.getJwtToken();
      const headers = {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        Authorization: jwt,
      };

      const body = {
        query: setSupplierAdmin,
        variables: {
          email: row.mail,
          vendorGroupNumber: row.vendorGroupNumber,
          isAdmin: !isCurrentlyAdmin,
        },
      };

      await axiosRequest({ headers, body, method: 'post' });

      triggerRefresh();
    } catch (error) {
      console.error('ERROR managing supplier admin: ', error);
      setLoading(false);
      setConfig({
        variation: 'error',
        msg: 'Error managing supplier admin, please contact admin',
      });
    }
  };

  const manageActions = () => {
    switch (managing) {
      case 'status': {
        const email = currentRow.mail;
        currentRow.active ? deactivateUser(email) : activateUser(email);
        break;
      }
      case 'admin':
        currentRow.vendorName === 'INTERNAL'
          ? promoteUser(currentRow)
          : demoteUser(currentRow);
        break;
      case 'supplierAdmin':
        manageSupplierAdmin(currentRow);
        break;

      default:
        break;
    }
  };

  return (
    <div id="user-list-body">
      <div className="alert-banner">
        <AmplifyAlertBanner isDismissible={true} config={config} />
      </div>
      <div className="manage-control">
        <div>
          <h2 className="manage-users-title">
            {CMS.pages.find((o) => o.name === 'manageUsers')?.title}
          </h2>
          <ToggleButtonGroup
            className="toggle-button-mode"
            value={showInternalUsers}
            exclusive
            sx={{ marginRight: '10px' }}
            onChange={handleUserTypeToggle}
          >
            <ToggleButton className="dq-toggle-button" value={false}>
              External
            </ToggleButton>
            <ToggleButton className="dq-toggle-button" value={true}>
              Internal
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        {showInternalUsers ? null : (
          <Button
            size="small"
            onClick={() => {
              navigate('/create-user');
            }}
          >
            Add New User
          </Button>
        )}
      </div>

      <Input.Search
        className="search-component"
        placeholder="Search Users"
        allowClear
        value={searchTerm}
        onSearch={handleSearch}
        onChange={handleSearchChange}
      />
      <Table
        className="users-table"
        columns={showInternalUsers ? columnsInternal : columns}
        bordered
        size="small"
        dataSource={filteredData}
        pagination={{
          position: ['bottomLeft'],
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['8', '20', '50'],
        }}
        loading={loading}
        onChange={handleTableChange}
      />

      <Modal
        open={showConfirmationModal}
        onOk={() => manageActions()}
        onCancel={() => handleModal(false)}
      >
        <p>{modalMsg}</p>
      </Modal>
    </div>
  );
};
